import {
  CHAIR_MATERIALS,
  formatChairMaterialLabel,
  listingTitleMaterialWord,
  type ChairMaterial,
} from "@/lib/listing/chairListingSpec";
import {
  composeListingTitleFromSlotsWithIdentity,
  joinListingTitleParts,
  type ListingTitleIdentity,
} from "@/lib/listing/listingTitleTokens";
import { asListingAttributesRecord } from "@/lib/listing/listingAttributesJson";
import type {
  ListingSpecAttributes,
  ListingSpecValidationIssue,
} from "@/lib/listing/specRequirements";

export const DINING_TABLE_CHAIR_SUBCATEGORY_ID = "dining-table-chair" as const;

export const DINING_CHAIR_COUNTS = [3, 4, 5, 6] as const;
export type DiningChairCount = (typeof DINING_CHAIR_COUNTS)[number];

export const DINING_TABLE_TOP_MATERIALS = [
  "glass",
  "marble",
  "wood",
  "granite",
  "other",
] as const;
export type DiningTableTopMaterial =
  (typeof DINING_TABLE_TOP_MATERIALS)[number];

export interface DiningTableChairSpecFormState {
  diningChairCount: DiningChairCount | "";
  diningTableTopMaterial: DiningTableTopMaterial | "";
  chairMaterial: ChairMaterial | "";
}

export function defaultDiningTableChairSpecFormState(): DiningTableChairSpecFormState {
  return {
    diningChairCount: "",
    diningTableTopMaterial: "",
    chairMaterial: "",
  };
}

export function isDiningTableChairSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return subcategoryId === DINING_TABLE_CHAIR_SUBCATEGORY_ID;
}

export function formatDiningTableTopMaterialLabel(
  material: DiningTableTopMaterial,
): string {
  return material.charAt(0).toUpperCase() + material.slice(1);
}

function isDiningChairCount(value: unknown): value is DiningChairCount {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    (DINING_CHAIR_COUNTS as readonly number[]).includes(value)
  );
}

export function isDiningTableTopMaterial(
  value: unknown,
): value is DiningTableTopMaterial {
  return (
    typeof value === "string" &&
    (DINING_TABLE_TOP_MATERIALS as readonly string[]).includes(value)
  );
}

/** Materials that get an exclusive pricing band (D8: `other` does not). */
export const DINING_TABLE_TOP_EXCLUSIVE_MATERIALS = [
  "glass",
  "marble",
  "wood",
  "granite",
] as const;
export type DiningTableTopExclusiveMaterial =
  (typeof DINING_TABLE_TOP_EXCLUSIVE_MATERIALS)[number];

export function isDiningTableTopExclusiveMaterial(
  value: unknown,
): value is DiningTableTopExclusiveMaterial {
  return (
    typeof value === "string" &&
    (DINING_TABLE_TOP_EXCLUSIVE_MATERIALS as readonly string[]).includes(value)
  );
}

/**
 * Infer table-top material from title (pricing / stamp).
 * Strict `{material}[- ]?top` only — no bare-word infer (Phase 0 D5).
 * Multi-hit priority: glass > marble > granite > wood (`wooden` → wood).
 */
export function inferDiningTableTopMaterialFromTitle(
  title: string | null | undefined,
): DiningTableTopExclusiveMaterial | null {
  if (!title?.trim()) return null;
  const t = title;
  if (/\bglass[- ]?top\b/i.test(t)) return "glass";
  if (/\bmarble[- ]?top\b/i.test(t)) return "marble";
  if (/\bgranite[- ]?top\b/i.test(t)) return "granite";
  if (/\b(?:wood|wooden)[- ]?top\b/i.test(t)) return "wood";
  return null;
}

function isChairMaterial(value: unknown): value is ChairMaterial {
  return (
    typeof value === "string" &&
    (CHAIR_MATERIALS as readonly string[]).includes(value)
  );
}

export function buildDiningTableChairAttributesFromForm(
  form: DiningTableChairSpecFormState,
): ListingSpecAttributes {
  if (
    !isDiningChairCount(form.diningChairCount) ||
    !isDiningTableTopMaterial(form.diningTableTopMaterial) ||
    !isChairMaterial(form.chairMaterial)
  ) {
    const partial: ListingSpecAttributes = {};
    if (isDiningChairCount(form.diningChairCount)) {
      partial.dining_chair_count = form.diningChairCount;
    }
    if (isDiningTableTopMaterial(form.diningTableTopMaterial)) {
      partial.dining_table_top_material = form.diningTableTopMaterial;
    }
    if (isChairMaterial(form.chairMaterial)) {
      partial.chair_material = form.chairMaterial;
    }
    return partial;
  }

  return {
    dining_chair_count: form.diningChairCount,
    dining_table_top_material: form.diningTableTopMaterial,
    chair_material: form.chairMaterial,
  };
}

export function diningTableChairFormFromAttributes(
  attributes: unknown,
): DiningTableChairSpecFormState {
  const base = defaultDiningTableChairSpecFormState();
  const record = asListingAttributesRecord(attributes);
  if (!record) {
    return base;
  }
  return {
    diningChairCount: isDiningChairCount(record.dining_chair_count)
      ? record.dining_chair_count
      : "",
    diningTableTopMaterial: isDiningTableTopMaterial(
      record.dining_table_top_material,
    )
      ? record.dining_table_top_material
      : "",
    chairMaterial: isChairMaterial(record.chair_material)
      ? record.chair_material
      : "",
  };
}

export function validateStoredDiningTableChairAttributes(
  attributes: ListingSpecAttributes | Record<string, unknown>,
): ListingSpecValidationIssue[] {
  const issues: ListingSpecValidationIssue[] = [];
  const record = attributes as Record<string, unknown>;

  if (!isDiningChairCount(record.dining_chair_count)) {
    issues.push({
      key: "dining_chair_count",
      code: "required",
      message: "Please choose how many chairs are included (3–6).",
    });
  }

  if (!isDiningTableTopMaterial(record.dining_table_top_material)) {
    issues.push({
      key: "dining_table_top_material",
      code: "required",
      message: "Table top material is required.",
    });
  }

  if (!isChairMaterial(record.chair_material)) {
    issues.push({
      key: "chair_material",
      code: "required",
      message: "Chair material is required.",
    });
  }

  return issues;
}

export function getDiningTableChairSpecFormValidationMessage(
  form: DiningTableChairSpecFormState,
): string | undefined {
  if (!form.diningChairCount) {
    return "Please choose how many chairs are included (3–6).";
  }
  if (!form.diningTableTopMaterial) {
    return "Table top material is required.";
  }
  if (!form.chairMaterial) {
    return "Chair material is required.";
  }

  const attrs = buildDiningTableChairAttributesFromForm(form);
  return validateStoredDiningTableChairAttributes(attrs)[0]?.message;
}

export function composeDiningTableChairTitleFromAttributes(
  attributes: ListingSpecAttributes | Record<string, unknown>,
  identity?: ListingTitleIdentity | null,
): string {
  const record = attributes as Record<string, unknown>;
  if (
    !isDiningChairCount(record.dining_chair_count) ||
    !isDiningTableTopMaterial(record.dining_table_top_material) ||
    !isChairMaterial(record.chair_material)
  ) {
    return "";
  }

  const topWord = listingTitleMaterialWord(
    record.dining_table_top_material,
    formatDiningTableTopMaterialLabel,
  );
  const chairWord = listingTitleMaterialWord(
    record.chair_material,
    formatChairMaterialLabel,
  );
  const topSegment = topWord ? `${topWord}-top` : "";
  const chairSegment = chairWord ? `${chairWord} Chairs` : "Chairs";
  return composeListingTitleFromSlotsWithIdentity(
    {
      product: joinListingTitleParts(
        record.dining_chair_count,
        "Seater",
        topSegment,
        "Dining Table and",
        chairSegment,
      ),
    },
    identity,
  );
}
